// Logger centralizado con nivel configurable.
//
// Precedencia del nivel (de mayor a menor prioridad):
//   1. localStorage 'logLevel'  -> override en runtime, por navegador, SIN redeploy.
//   2. REACT_APP_LOG_LEVEL      -> default del build/ambiente (.env.*), para quien pueda setearlo.
//   3. DEFAULT_LOG_LEVEL        -> default controlado desde el repo (src/utils/logger.config.ts).
//                                  NO depende de infra/.env; es el que se edita para PRODUCCIÓN.
//   4. NODE_ENV                 -> fallback duro: 'warn' en producción, 'debug' en desarrollo.
//
// Niveles válidos: 'debug' | 'info' | 'warn' | 'error' | 'silent'.
//
// Cómo activar debug en PRODUCCIÓN (solo tu navegador, sin redeploy):
//   localStorage.setItem('logLevel', 'debug'); location.reload();
// Para volver al comportamiento normal:
//   localStorage.removeItem('logLevel'); location.reload();
//
// Cómo cambiar el default de PRODUCCIÓN: editá DEFAULT_LOG_LEVEL en logger.config.ts y redeployá.
// Cómo silenciar tu LOCAL: definí REACT_APP_LOG_LEVEL en .env.development.local.
//
// Nota: el nivel se resuelve en runtime (lee localStorage), por eso Terser NO puede
// eliminar las llamadas a console.* del bundle de producción; quedan presentes pero
// gateadas (no se ejecutan salvo que el nivel activo lo permita).
import { DEFAULT_LOG_LEVEL, LogLevel } from './logger.config';

export type { LogLevel };

const LEVELS: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 99,
};

const isLogLevel = (v: unknown): v is LogLevel =>
  typeof v === 'string' && v in LEVELS;

const resolveLevel = (): number => {
  // 1. Override en runtime por localStorage.
  try {
    const override = window.localStorage.getItem('logLevel');
    if (isLogLevel(override)) return LEVELS[override];
  } catch {
    // localStorage puede no estar disponible (modo privado, etc.) -> se ignora.
  }

  // 2. Default del build/ambiente (para quien pueda setear .env).
  const configured = process.env.REACT_APP_LOG_LEVEL;
  if (isLogLevel(configured)) return LEVELS[configured];

  // 3. Default controlado desde el repo (logger.config.ts) — NO depende de infra.
  if (isLogLevel(DEFAULT_LOG_LEVEL)) return LEVELS[DEFAULT_LOG_LEVEL];

  // 4. Fallback duro según NODE_ENV.
  return process.env.NODE_ENV === 'production' ? LEVELS.warn : LEVELS.debug;
};

const current = resolveLevel();

const noop = (..._args: unknown[]): void => {};

// Se usa .bind(console) en vez de envolver en una función propia para que las
// DevTools sigan mostrando el archivo/línea real de origen (un wrapper haría
// que todo apunte a logger.ts).
const at = (lvl: number, fn: (...args: any[]) => void) =>
  current <= lvl ? fn : noop;

// Extrae solo campos NO sensibles de un error (incluye errores de axios).
// Evita filtrar lo que un error de axios arrastra: config.headers
// (Authorization/token), cookies, response.data y response.headers completos.
// Usar siempre en lugar de logear el error crudo:  logger.error('...', safeError(e))
export const safeError = (err: unknown): Record<string, unknown> => {
  if (err && typeof err === 'object') {
    const e = err as {
      message?: unknown;
      name?: unknown;
      code?: unknown;
      status?: unknown;
      response?: { status?: unknown };
      config?: { method?: unknown; url?: unknown };
    };
    const safe: Record<string, unknown> = {
      message: e.message,
      name: e.name,
      code: e.code,
      status: e.response?.status ?? e.status,
      method: e.config?.method,
      url: e.config?.url,
    };
    Object.keys(safe).forEach((k) => safe[k] === undefined && delete safe[k]);
    return safe;
  }
  return { value: String(err) };
};

export const logger = {
  log: at(LEVELS.debug, console.log.bind(console)),
  debug: at(LEVELS.debug, console.debug.bind(console)),
  info: at(LEVELS.info, console.info.bind(console)),
  warn: at(LEVELS.warn, console.warn.bind(console)),
  error: at(LEVELS.error, console.error.bind(console)),
  safeError,
};

export default logger;
